import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { zafClient, searchProductsAutocomplete } from "../../services/Zendesk";
import type {
  InputSearchProps,
  SearchItem,
  ProductResult,
  CustomObjectRecord,
} from "./interfaces";

const uniqById = <T extends { id: string }>(arr: T[]) =>
  Array.from(new Map(arr.map((u) => [u.id, u])).values());

async function fetchProdutoRecordsAll(objectKey: string) {
  const url = `/api/v2/custom_objects/${objectKey}/records`;
  const res = (await zafClient.request({
    url,
    type: "GET",
  })) as { custom_object_records?: CustomObjectRecord[] };
  return res?.custom_object_records ?? [];
}

const normalizeProduct = (r: CustomObjectRecord): ProductResult => ({
  kind: "product",
  id: r.id,
  name:
    r.name ||
    (r.custom_object_fields?.produto_comercial as string) ||
    (r.custom_object_fields?.nome as string) ||
    (r.custom_object_fields?.descricao_do_produto as string) ||
    "(sem nome)",
  custom_object_key: "produto",
  custom_object_fields: r.custom_object_fields,
});

// Função de busca usando o autocomplete nativo do Zendesk
export const zendeskAutocompleteProductSearchFn = async (
  term: string
): Promise<ProductResult[]> => {
  try {
    console.log("🔍 Usando autocomplete nativo do Zendesk para:", term);
    const results = await searchProductsAutocomplete(term);
    
    const products: ProductResult[] = results.map(result => ({
      kind: "product" as const,
      id: String(result.id),
      name: result.label,
      custom_object_key: "produto" as const,
      custom_object_fields: result.raw.custom_object_fields || {},
    }));

    console.log("✅ Produtos encontrados via autocomplete:", products.length);
    return products;
  } catch (error) {
    console.error("❌ Erro no autocomplete Zendesk, usando busca manual:", error);
    // Fallback para a busca manual
    return await defaultProductSearchFn(term);
  }
};

export const defaultProductSearchFn = async (
  term: string,
  objectTypeKey = "produto"
): Promise<ProductResult[]> => {
  const t = term?.trim();
  if (!t) return [];

  const all = await fetchProdutoRecordsAll(objectTypeKey);
  
  // Log temporário para debug
  const targetProduct = all.find(r => 
    r.name?.includes("TV PTV32E20DSGWA") || 
    String(r.custom_object_fields?.codigo_do_produto).includes("99323048")
  );
  if (targetProduct) {
    console.log("🔍 DEBUG: Produto alvo encontrado (NewOrder):", {
      id: targetProduct.id,
      name: targetProduct.name,
      custom_object_fields: targetProduct.custom_object_fields
    });
  }

  const exact = all.filter(
    (r) => {
      const codigo = String(r.custom_object_fields?.codigo_do_produto ?? "").trim();
      const codigoNormalized = codigo.toLowerCase().replace(/\s+/g, "");
      const termNormalized = t.toLowerCase().replace(/\s+/g, "");
      
      // Log para debug do código específico
      if (codigo.includes("99323048") || termNormalized.includes("99323048")) {
        console.log("🔍 DEBUG: Código encontrado (NewOrder):", {
          codigo: codigo,
          codigoNormalized: codigoNormalized,
          termNormalized: termNormalized,
          matches: codigoNormalized === termNormalized
        });
      }
      
      return codigoNormalized === termNormalized;
    }
  );
  if (exact.length) {
    return uniqById(exact.map(normalizeProduct));
  }

  const lc = t.toLowerCase();
  const searchTerms = lc.split(/\s+/).filter((term: string) => term.length > 0);
  
  const filtered = all.filter((r) => {
    const f = r.custom_object_fields || ({} as Record<string, unknown>);
    
    // Lista expandida de campos para busca - com normalização especial para códigos
    const searchableFields = [
      r.name,
      f.produto_comercial,
      f.descricao_do_produto,
      String(f.codigo_do_produto || ""),
      f.referencia_telecontrol,
      f.modelo,
      f.familia_do_produto,
      f.familia_produto,
      f.marca,
      f.nome,
      f.descricao,
      String(f.codigo || ""),
      f.tipo_de_registro,
      f.linha,
      f.produto,
      f.sku_do_produto,
      f.name,
      f.title,
      // Campos específicos que vi na imagem
      String(f.codigo_erp || ""),
      f.valor_base,
      String(f.peso || ""),
      String(f.altura || ""),
      String(f.largura || ""),
      String(f.profundidade || ""),
      f.origem,
      f.engenharia,
      f.lamina,
      // Tratamento especial para números que podem estar como string
      ...(Object.entries(f)
        .filter(([key, val]) => 
          (typeof val === 'string' || typeof val === 'number') &&
          String(val).length > 0 && 
          String(val).length < 200 &&
          !key.includes('http') && // Ignorar URLs
          !key.includes('image') && // Ignorar campos de imagem
          !key.includes('data') && // Ignorar datas
          !key.includes('inicio') // Ignorar datas de início
        )
        .map(([, val]) => String(val)))
    ];
    
    const searchText = searchableFields
      .filter(Boolean)
      .map(field => String(field).trim())
      .join(" ")
      .toLowerCase()
      .replace(/[^\w\s]/g, " ") // Remove caracteres especiais
      .replace(/\s+/g, " ") // Normaliza espaços
      .trim();
    
    // Log para o produto específico que queremos encontrar
    if (searchText.includes("tv ptv32e20dsgwa") || searchText.includes("99323048")) {
      console.log("🔍 DEBUG: Produto específico analisado (NewOrder):", {
        name: r.name,
        codigo_produto: f.codigo_do_produto,
        searchText: searchText,
        searchTerms: searchTerms,
        matches: searchTerms.map((term: string) => ({ term, found: searchText.includes(term) }))
      });
    }
    
    // Busca específica para códigos numéricos
    if (/^\d+$/.test(lc)) {
      const codigoMatch = searchableFields.some(field => 
        String(field || "").replace(/\D/g, "").includes(lc)
      );
      if (codigoMatch) {
        console.log("🔍 DEBUG: Match por código numérico encontrado (NewOrder)!");
        return true;
      }
    }
    
    // Busca por todos os termos - deve conter TODOS os termos digitados
    if (searchTerms.length > 1) {
      return searchTerms.every((term: string) => searchText.includes(term));
    }
    
    // Busca simples para um único termo
    return searchText.includes(lc);
  });

  return uniqById(filtered.map(normalizeProduct));
};

function useDebouncedCallback(
  fn: (term: string) => Promise<void>,
  delay: number
) {
  const timer = useRef<number | null>(null);
  const cb = useCallback(
    (term: string) => {
      if (timer.current) window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => fn(term), delay);
    },
    [fn, delay]
  );
  useEffect(
    () => () => {
      if (timer.current) window.clearTimeout(timer.current);
    },
    []
  );
  return cb;
}

export function useProductSearch({
  searchFn,
  minChars = 2,
  debounceMs = 300,
  initialValue = "",
  onSelect,
  objectTypeKey = "produto",
  useZendeskAutocomplete = true, // Nova prop para escolher o método
}: InputSearchProps & {
  useZendeskAutocomplete?: boolean;
}) {
  const [query, setQuery] = useState(initialValue);
  const [results, setResults] = useState<SearchItem[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [highlight, setHighlight] = useState<number>(-1);
  const [error, setError] = useState<string | null>(null);

  const finalSearchFn = useMemo(() => {
    if (searchFn) return searchFn;
    
    // Escolhe entre autocomplete nativo ou busca manual
    if (useZendeskAutocomplete) {
      return zendeskAutocompleteProductSearchFn;
    }
    
    return (term: string) => defaultProductSearchFn(term, objectTypeKey);
  }, [searchFn, objectTypeKey, useZendeskAutocomplete]);

  const doSearch = useCallback(
    async (term: string) => {
      if (term.trim().length < minChars) {
        setResults([]);
        setOpen(false);
        setLoading(false);
        setError(null);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const list = await finalSearchFn(term.trim());
        setResults(list);
        setOpen(true);
        setHighlight(list.length ? 0 : -1);
      } catch (err) {
        const error = err as {
          response?: { data?: { error?: string } };
          message?: string;
        };
        const msg =
          error?.response?.data?.error || error?.message || "Falha na busca";
        setError(msg);
        setResults([]);
        setOpen(true);
        setHighlight(-1);
      } finally {
        setLoading(false);
      }
    },
    [finalSearchFn, minChars]
  );

  const debouncedSearch = useDebouncedCallback(doSearch, debounceMs);

  const handleSearchChange = (value: string) => {
    setQuery(value);
    debouncedSearch(value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlight >= 0 && highlight < results.length) {
        const picked = results[highlight];
        onSelect(picked);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const handleBlur = () => setTimeout(() => setOpen(false), 200);

  const handleSelect = (item: SearchItem) => {
    onSelect(item);
    setQuery("");
    setOpen(false);
  };

  const handleClearClick = () => {
    setQuery("");
    setResults([]);
    setOpen(false);
  };

  return {
    query,
    results,
    open,
    loading,
    highlight,
    error,
    onSearchChange: handleSearchChange,
    onKeyDown: handleKeyDown,
    onBlur: handleBlur,
    onSelect: handleSelect,
    handleClearClick,
  };
}
